import { BlobCtaButton } from "@cupcodev/ui";
import type { CustomerPlanUpgrade, StepState } from "../../shared/upgrades/upgrades";
import { upgradeSteps } from "../../shared/upgrades/upgrades";
import { NaveIcon, Notice } from "../../ui/NavePage";
import { useI18n } from "../i18n/I18nProvider";


export const isUpgradeInFlight = (upgrade: CustomerPlanUpgrade | null | undefined): upgrade is CustomerPlanUpgrade =>
  upgrade !== null && upgrade !== undefined && ["creating", "awaiting_signature", "awaiting_payment", "active"].includes(upgrade.status);

/**
 * The three upgrade steps with what the customer has to do now. Contract, signature and payment all
 * happen on Stargate/Cashier surfaces; this only reports their state and links to the next action.
 */
export const UpgradeProgress = ({ upgrade, busy, sendFailed, onResend, onPay }: {
  upgrade: CustomerPlanUpgrade;
  busy: boolean;
  sendFailed: boolean;
  onResend(): void;
  onPay(): void;
}) => {
  const { t, formatCurrency } = useI18n();
  const [sign, pay, active] = upgradeSteps(upgrade.status);
  const amount = formatCurrency(upgrade.quote.amountDueToday, upgrade.quote.currency);
  const params = { target: upgrade.quote.targetLabel, amount, email: upgrade.signer?.email ?? "" };
  const icon = (state: StepState): string => (state === "done" ? "lucide:check" : state === "current" ? "lucide:loader-circle" : "lucide:circle");
  return (
    <div className="nave-upgrade">
      <ol className="nave-upgrade-steps" aria-label={t("upgrade.progress.label")}>
        <li data-state={sign}>
          <span className="nave-upgrade-step-icon"><NaveIcon name={icon(sign)} /></span>
          <span className="nave-upgrade-step-copy">
            <strong>{t(sign === "done" ? "upgrade.progress.signed" : "upgrade.progress.sign")}</strong>
            {sign === "current" ? <small>{t(sendFailed ? "upgrade.progress.signSendFailed" : "upgrade.progress.signDetail", params)}</small> : null}
            {sign === "current" ? <span className="nave-upgrade-step-action">
              <BlobCtaButton type="button" size="sm" variant="secondary" loading={busy} disabled={busy} onClick={onResend}>{t(sendFailed ? "upgrade.progress.send" : "upgrade.progress.resend")}</BlobCtaButton>
            </span> : null}
          </span>
        </li>
        <li data-state={pay}>
          <span className="nave-upgrade-step-icon"><NaveIcon name={icon(pay)} /></span>
          <span className="nave-upgrade-step-copy">
            <strong>{t(pay === "done" ? "upgrade.progress.paid" : "upgrade.progress.pay", params)}</strong>
            {pay === "current" ? <small>{t("upgrade.progress.payDetail")}</small> : null}
            {pay === "current" ? <span className="nave-upgrade-step-action">
              <BlobCtaButton type="button" size="sm" onClick={onPay}>{t("upgrade.progress.payNow", params)}</BlobCtaButton>
            </span> : null}
          </span>
        </li>
        <li data-state={active}>
          <span className="nave-upgrade-step-icon"><NaveIcon name={icon(active)} /></span>
          <span className="nave-upgrade-step-copy">
            <strong>{t(active === "done" ? "upgrade.progress.active" : "upgrade.progress.activate", params)}</strong>
            {active === "done" ? <small>{t("upgrade.progress.activeDetail")}</small> : null}
          </span>
        </li>
      </ol>
      {upgrade.status === "awaiting_signature" ? <Notice icon="lucide:mail">{t("upgrade.progress.checkInbox")}</Notice> : null}
    </div>
  );
};
